import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import {
  collection,
  addDoc,
  getDocs,
  Timestamp,
  query,
  orderBy,
  updateDoc,
  doc,
  deleteDoc
} from 'firebase/firestore';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import 'chart.js/auto';
import './AdminExpenses.css';
import { FaEdit, FaTrash, FaPlus, FaArchive, FaUndo, FaEye, FaEyeSlash, FaCheckCircle, FaCircle, FaSearch, FaChartBar } from 'react-icons/fa';

const EXPENSES_COLLECTION = 'admin_expenses';
const CHEQUES_COLLECTION = 'admin_cheques';
const RECEIVED_CHEQUES_COLLECTION = 'received_cheques';

function daysUntil(date) {
  const now = new Date();
  const target = new Date(date);
  const diff = target - now;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

const todayStr = () => new Date().toISOString().substring(0, 10);

// Helper function para convertir fecha string a Timestamp de Firestore para Argentina
const createFirestoreDate = (dateString) => {
  // Crear fecha en zona horaria local de Argentina
  const date = new Date(dateString + 'T00:00:00');
  return Timestamp.fromDate(date);
};

// Helper function para convertir Timestamp de Firestore a Date local
const getLocalDateFromFirestore = (firestoreDate) => {
  if (!firestoreDate) return new Date();
  if (firestoreDate.seconds) {
    return new Date(firestoreDate.seconds * 1000);
  }
  return new Date(firestoreDate);
};

// Helper function para obtener fecha en formato YYYY-MM-DD desde Firestore
const getDateStringFromFirestore = (firestoreDate) => {
  const date = getLocalDateFromFirestore(firestoreDate);
  return date.toISOString().substring(0, 10);
};

// Helper para obtener el medio de pago con valor por defecto
const getPaymentMethod = (expense) => {
  return (expense && expense.paymentMethod) ? expense.paymentMethod : 'Efectivo';
};

// Helper para obtener cuotas con valor por defecto 1
const getInstallments = (expense) => {
  const val = expense && (expense.installments ?? 1);
  const n = parseInt(val, 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
};

// Utilidades de fechas
const formatYearMonth = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
const addMonths = (date, monthsToAdd) => {
  const d = new Date(date.getTime());
  d.setMonth(d.getMonth() + monthsToAdd);
  return d;
};

// Color del badge de cuotas según cantidad
const getInstallmentsColor = (installments) => {
  switch (parseInt(installments, 10)) {
    case 1:
      return '#10b981'; // verde
    case 3:
      return '#3b82f6'; // azul
    case 6:
      return '#f59e0b'; // ámbar
    case 12:
      return '#ef4444'; // rojo
    default:
      return '#6b7280'; // gris
  }
};

// Componente de Checkbox personalizado mejorado
const CustomCheckbox = ({ checked, onChange, disabled = false, size = 'normal' }) => (
  <label className={`expenses-checkbox ${size === 'small' ? 'small' : ''}`}>
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
    <span className="checkmark"></span>
  </label>
);

// Componente para toggle de estado mejorado
const StatusToggle = ({ checked, onChange, disabled, type = 'paid', size = 'normal' }) => {
  const getConfig = () => {
    switch (type) {
      case 'paid':
        return {
          trueIcon: <FaCheckCircle />,
          falseIcon: <FaCircle />,
          trueTitle: 'Marcar como no pagado',
          falseTitle: 'Marcar como pagado',
          className: 'status-paid'
        };
      case 'deposited':
        return {
          trueIcon: <FaCheckCircle />,
          falseIcon: <FaCircle />,
          trueTitle: 'Marcar como no depositado',
          falseTitle: 'Marcar como depositado',
          className: 'status-deposited'
        };
      default:
        return {
          trueIcon: <FaCheckCircle />,
          falseIcon: <FaCircle />,
          trueTitle: 'Desmarcar',
          falseTitle: 'Marcar',
          className: 'status-default'
        };
    }
  };

  const config = getConfig();

  return (
    <button
      type="button"
      className={`status-toggle ${config.className} ${checked ? 'active' : ''} ${size === 'small' ? 'small' : ''}`}
      onClick={onChange}
      disabled={disabled}
      title={checked ? config.trueTitle : config.falseTitle}
    >
      {checked ? config.trueIcon : config.falseIcon}
    </button>
  );
};

// Componente para badge de estado mejorado
const StatusBadge = ({ dueDate, archived = false }) => {
  if (archived) {
    return <span className="expenses-badge archived">📦 Archivado</span>;
  }

  const venc = new Date(dueDate.seconds ? dueDate.seconds * 1000 : dueDate);
  const dias = daysUntil(venc);

  if (dias < 0) {
    return <span className="expenses-badge danger">❗ Vencido ({Math.abs(dias)}d)</span>;
  } else if (dias === 0) {
    return <span className="expenses-badge urgent">🔥 Vence hoy</span>;
  } else if (dias <= 3) {
    return <span className="expenses-badge warning">⏰ {dias} días</span>;
  } else if (dias <= 7) {
    return <span className="expenses-badge attention">⚠️ {dias} días</span>;
  } else if (dias <= 15) {
    return <span className="expenses-badge info">ℹ️ {dias} días</span>;
  } else {
    return <span className="expenses-badge success">✅ {dias} días</span>;
  }
};

const AdminExpenses = ({ showNotification }) => {
  // Estados principales
  const [activeTab, setActiveTab] = useState('expenses');
  // Carga diferida de gráficos: por defecto se ocultan para evitar renderizar
  // todos los canvas y recálculos pesados cuando solo se quiere cargar/ver un gasto.
  const [showCharts, setShowCharts] = useState(false);

  // Estados para gastos
  const [expenseForm, setExpenseForm] = useState({
    category: '',
    amount: '',
    date: todayStr(),
    description: '',
    paymentMethod: 'Efectivo',
    installments: 1,
    excludeFromTotals: false,
    search: '' // Nuevo estado para el buscador
  });
  const [expenses, setExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);

  // Estados para cheques emitidos
  const [chequeForm, setChequeForm] = useState({
    name: '',
    banco: '',
    number: '',
    amount: '',
    createdAt: todayStr(),
    dueDate: ''
  });
  const [cheques, setCheques] = useState([]);
  const [loadingCheques, setLoadingCheques] = useState(false);
  const [selectedCheques, setSelectedCheques] = useState(new Set());

  // Estados para cheques recibidos
  const [receivedChequeForm, setReceivedChequeForm] = useState({
    name: '',
    banco: '',
    number: '',
    amount: '',
    receivedAt: todayStr(),
    dueDate: ''
  });
  const [receivedCheques, setReceivedCheques] = useState([]);
  const [loadingReceivedCheques, setLoadingReceivedCheques] = useState(false);
  const [selectedReceivedCheques, setSelectedReceivedCheques] = useState(new Set());

  // Estados adicionales
  const [processingCheques, setProcessingCheques] = useState(new Set());
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [editExpense, setEditExpense] = useState(null);
  const [editExpenseForm, setEditExpenseForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [showArchivedCheques, setShowArchivedCheques] = useState(false);
  const [showArchivedReceivedCheques, setShowArchivedReceivedCheques] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [excludedFilter, setExcludedFilter] = useState('Incluidos'); // 'Incluidos' | 'Excluidos' | 'Todos'
  const [selectedExpenses, setSelectedExpenses] = useState(new Set());
  const [editCheque, setEditCheque] = useState(null);
  const [editChequeForm, setEditChequeForm] = useState(null);
  const [savingChequeEdit, setSavingChequeEdit] = useState(false);
  const [editChequeType, setEditChequeType] = useState(null); // 'emitted' o 'received'
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().substring(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().substring(0, 10);
  });

  // Estados para comparación mensual
  const [comparisonYear, setComparisonYear] = useState(() => {
    const now = new Date();
    return now.getFullYear();
  });

  // Funciones fetch
  const fetchExpenses = useCallback(async () => {
    setLoadingExpenses(true);
    try {
      const q = query(collection(db, EXPENSES_COLLECTION), orderBy('date', 'desc'));
      const snap = await getDocs(q);
      setExpenses(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (e) {
      showNotification('Error al cargar gastos', 'error');
    }
    setLoadingExpenses(false);
  }, [showNotification]);

  const fetchCheques = useCallback(async () => {
    setLoadingCheques(true);
    try {
      const q = query(collection(db, CHEQUES_COLLECTION), orderBy('dueDate', 'asc'));
      const snap = await getDocs(q);
      const chequesData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCheques(chequesData);
    } catch (e) {
      showNotification('Error al cargar cheques', 'error');
    }
    setLoadingCheques(false);
  }, [showNotification]);

  const fetchReceivedCheques = useCallback(async () => {
    setLoadingReceivedCheques(true);
    try {
      const q = query(collection(db, RECEIVED_CHEQUES_COLLECTION), orderBy('dueDate', 'asc'));
      const snap = await getDocs(q);
      setReceivedCheques(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (e) {
      showNotification('Error al cargar cheques recibidos', 'error');
    }
    setLoadingReceivedCheques(false);
  }, [showNotification]);

  // useEffect que los usa
  useEffect(() => {
    fetchExpenses();
    fetchCheques();
    fetchReceivedCheques();
  }, [fetchExpenses, fetchCheques, fetchReceivedCheques]);

  useEffect(() => {
    if (activeTab === 'expenses') {
      setExpenseForm(f => ({ ...f, date: todayStr() }));
    }
  }, [activeTab]);

  // Datos derivados
  const usualExpenses = Array.from(new Set(
    expenses
      .map(e => (e.description || '').trim())
      .filter(desc => desc && desc.length > 2)
  ));

  const dynamicCategories = Array.from(new Set(
    expenses.map(e => (e.category || '').trim()).filter(cat => cat && cat.length > 2)
  ));

  // Handlers para gastos
  const handleExpenseChange = e => {
    const { name, value } = e.target;
    setExpenseForm(f => ({ ...f, [name]: value }));
  };

  const handleExpenseSubmit = async e => {
    e.preventDefault();
    if (!expenseForm.category || !expenseForm.amount || !expenseForm.date) {
      showNotification('Completa todos los campos obligatorios', 'error');
      return;
    }
    try {
      await addDoc(collection(db, EXPENSES_COLLECTION), {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
        date: createFirestoreDate(expenseForm.date),
        createdAt: Timestamp.now()
      });
      showNotification('Gasto registrado', 'success');
      setExpenseForm({ category: '', amount: '', date: todayStr(), description: '', paymentMethod: 'Efectivo', installments: 1, excludeFromTotals: false, search: '' });
      fetchExpenses();
    } catch (e) {
      showNotification('Error al guardar gasto', 'error');
    }
  };

  // Handlers para cheques emitidos
  const handleChequeChange = e => {
    const { name, value } = e.target;
    setChequeForm(f => ({ ...f, [name]: value }));
  };

  const handleChequeSubmit = async e => {
    e.preventDefault();
    if (!chequeForm.name || !chequeForm.number || !chequeForm.amount || !chequeForm.createdAt || !chequeForm.dueDate) {
      showNotification('Completa todos los campos del cheque', 'error');
      return;
    }
    try {
      await addDoc(collection(db, CHEQUES_COLLECTION), {
        ...chequeForm,
        amount: parseFloat(chequeForm.amount),
        createdAt: createFirestoreDate(chequeForm.createdAt),
        dueDate: createFirestoreDate(chequeForm.dueDate),
        registeredAt: Timestamp.now()
      });
      showNotification('Cheque registrado', 'success');
      setChequeForm({ name: '', banco: '', number: '', amount: '', createdAt: todayStr(), dueDate: '' });
      fetchCheques();
    } catch (e) {
      showNotification('Error al guardar cheque', 'error');
    }
  };

  // Handlers para cheques recibidos
  const handleReceivedChequeChange = e => {
    const { name, value } = e.target;
    setReceivedChequeForm(f => ({ ...f, [name]: value }));
  };

  const handleReceivedChequeSubmit = async e => {
    e.preventDefault();
    if (!receivedChequeForm.name || !receivedChequeForm.number || !receivedChequeForm.amount || !receivedChequeForm.receivedAt || !receivedChequeForm.dueDate) {
      showNotification('Completa todos los campos del cheque recibido', 'error');
      return;
    }
    try {
      await addDoc(collection(db, RECEIVED_CHEQUES_COLLECTION), {
        ...receivedChequeForm,
        amount: parseFloat(receivedChequeForm.amount),
        receivedAt: createFirestoreDate(receivedChequeForm.receivedAt),
        dueDate: createFirestoreDate(receivedChequeForm.dueDate),
        registeredAt: Timestamp.now(),
        deposited: false
      });
      showNotification('Cheque recibido registrado', 'success');
      setReceivedChequeForm({ name: '', banco: '', number: '', amount: '', receivedAt: todayStr(), dueDate: '' });
      fetchReceivedCheques();
    } catch (e) {
      showNotification('Error al guardar cheque recibido', 'error');
    }
  };

  // Funciones para acciones de cheques mejoradas
  async function toggleChequeArchived(chequeId, isArchived, collection_name) {
    setProcessingCheques(prev => new Set(prev).add(chequeId));
    try {
      const chequeRef = doc(db, collection_name, chequeId);
      await updateDoc(chequeRef, { archived: !isArchived });
      showNotification(
        !isArchived ? 'Cheque archivado' : 'Cheque desarchivado',
        'success'
      );
      if (collection_name === CHEQUES_COLLECTION) {
        fetchCheques();
      } else {
        fetchReceivedCheques();
      }
    } catch (e) {
      showNotification('Error al actualizar cheque', 'error');
    }
    setProcessingCheques(prev => {
      const newSet = new Set(prev);
      newSet.delete(chequeId);
      return newSet;
    });
  }

  // Funciones de selección de cheques mejoradas
  const toggleChequeSelection = (chequeId) => {
    setSelectedCheques(prev => {
      const newSet = new Set(prev);
      if (newSet.has(chequeId)) {
        newSet.delete(chequeId);
      } else {
        newSet.add(chequeId);
      }
      return newSet;
    });
  };

  const toggleReceivedChequeSelection = (chequeId) => {
    setSelectedReceivedCheques(prev => {
      const newSet = new Set(prev);
      if (newSet.has(chequeId)) {
        newSet.delete(chequeId);
      } else {
        newSet.add(chequeId);
      }
      return newSet;
    });
  };

  const toggleAllCheques = (isChecked) => {
    if (isChecked) {
      const visibleCheques = cheques.filter(cheq => showArchivedCheques ? cheq.archived : !cheq.archived);
      setSelectedCheques(new Set(visibleCheques.map(cheq => cheq.id)));
    } else {
      setSelectedCheques(new Set());
    }
  };

  const toggleAllReceivedCheques = (isChecked) => {
    if (isChecked) {
      const visibleCheques = receivedCheques.filter(cheq => showArchivedReceivedCheques ? cheq.archived : !cheq.archived);
      setSelectedReceivedCheques(new Set(visibleCheques.map(cheq => cheq.id)));
    } else {
      setSelectedReceivedCheques(new Set());
    }
  };

  // Calcular totales
  const selectedChequesTotal = cheques
    .filter(cheque => selectedCheques.has(cheque.id))
    .reduce((total, cheque) => total + cheque.amount, 0);

  const selectedReceivedChequesTotal = receivedCheques
    .filter(cheque => selectedReceivedCheques.has(cheque.id))
    .reduce((total, cheque) => total + cheque.amount, 0);

  // Datos para gráficos
  const expensesByMonth = expenses.filter(exp => {
    const d = getLocalDateFromFirestore(exp.date);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    return ym === selectedMonth;
  });

  // Lógica de filtrado independiente para la tabla de gastos
  // Los filtros de fecha son independientes del selector de mes (que es solo para gráficos)
  let filteredExpenses = [];
  // Crear fechas con hora 00:00:00 para comparación exacta
  const fromDate = new Date(dateFrom + 'T00:00:00');
  const toDate = new Date(dateTo + 'T23:59:59');
  
  // Aplicar filtros sobre TODOS los gastos, no solo los del mes seleccionado
  // Base: se ajusta según filtro de excluidos
  let baseExpenses = expenses;
  if (excludedFilter === 'Incluidos') {
    baseExpenses = baseExpenses.filter(exp => !exp.excludeFromTotals);
  } else if (excludedFilter === 'Excluidos') {
    baseExpenses = baseExpenses.filter(exp => !!exp.excludeFromTotals);
  }
  
  // Filtrar por rango de fechas
  baseExpenses = baseExpenses.filter(exp => {
    const expDate = getLocalDateFromFirestore(exp.date);
    return expDate >= fromDate && expDate <= toDate;
  });
  // Filtrar por medio de pago si corresponde
  if (selectedPaymentMethod && selectedPaymentMethod !== 'Todos') {
    baseExpenses = baseExpenses.filter(exp => getPaymentMethod(exp) === selectedPaymentMethod);
  }
  
  if (searchTerm.trim() !== '') {
    const term = searchTerm.toLowerCase();
    filteredExpenses = baseExpenses.filter(exp => {
      return (
        (exp.category && exp.category.toLowerCase().includes(term)) ||
        (exp.description && exp.description.toLowerCase().includes(term)) ||
        (exp.amount && exp.amount.toString().includes(term)) ||
        (exp.date && getLocalDateFromFirestore(exp.date).toLocaleDateString('es-AR').includes(term))
      );
    });
  } else if (selectedCategory === '') {
    filteredExpenses = [];
  } else if (selectedCategory === 'Todas') {
    filteredExpenses = baseExpenses;
  } else {
    filteredExpenses = baseExpenses.filter(exp => {
      return exp.category === selectedCategory;
    });
  }

  const categoryTotals = {};
  let totalGeneral = 0;
  // Totales por categoría del mes seleccionado, respetando filtro de excluidos
  expensesByMonth.filter(exp => {
    if (excludedFilter === 'Incluidos') return !exp.excludeFromTotals;
    if (excludedFilter === 'Excluidos') return !!exp.excludeFromTotals;
    return true;
  }).forEach(exp => {
    categoryTotals[exp.category] = (categoryTotals[exp.category] || 0) + exp.amount;
    totalGeneral += exp.amount;
  });

  const totalFiltrado = filteredExpenses.reduce((acc, exp) => acc + exp.amount, 0);

  // Totales por medio de pago para el mes seleccionado
  const paymentMethodTotals = {};
  expensesByMonth.forEach(expense => {
    const method = getPaymentMethod(expense);
    paymentMethodTotals[method] = (paymentMethodTotals[method] || 0) + expense.amount;
  });

  const paymentMethodLabels = Object.keys(paymentMethodTotals);
  const paymentMethodData = paymentMethodLabels.map(label => paymentMethodTotals[label]);

  // Función para generar datos de comparación mensual por categoría
  const generateMonthlyComparisonData = () => {
    // Generar lista de meses en el rango seleccionado
    const months = Array.from({ length: 12 }, (_, i) => `${comparisonYear}-${String(i + 1).padStart(2, '0')}`);

    // Filtrar gastos en el rango de comparación
    const comparisonExpenses = expenses.filter(exp => {
      // Aplicar lógica de exclusión
      if (excludedFilter === 'Incluidos' && exp.excludeFromTotals) return false;
      if (excludedFilter === 'Excluidos' && !exp.excludeFromTotals) return false;
      const expDate = getLocalDateFromFirestore(exp.date);
      const expMonth = `${expDate.getFullYear()}-${String(expDate.getMonth() + 1).padStart(2, '0')}`;
      return expDate.getFullYear() === comparisonYear && months.includes(expMonth);
    });

    // Obtener todas las categorías únicas en el período
    const categories = Array.from(new Set(comparisonExpenses.map(exp => exp.category)));

    // Crear estructura de datos: {categoría: [mes1Total, mes2Total, ...]}
    const categoryData = {};
    categories.forEach(category => {
      categoryData[category] = months.map(month => {
        return comparisonExpenses
          .filter(exp => {
            const expDate = getLocalDateFromFirestore(exp.date);
            const expMonth = `${expDate.getFullYear()}-${String(expDate.getMonth() + 1).padStart(2, '0')}`;
            return expMonth === month && exp.category === category;
          })
          .reduce((total, exp) => total + exp.amount, 0);
      });
    });

    return { months, categories, categoryData };
  };

  // Solo se calcula cuando los gráficos están visibles (evita loops innecesarios)
  const comparisonData = showCharts
    ? generateMonthlyComparisonData()
    : { months: [], categories: [], categoryData: {} };

  // Preparar datos para el gráfico de líneas
  const lineChartData = {
    labels: comparisonData.months.map(month => {
      const [year, monthNum] = month.split('-');
      const date = new Date(parseInt(year), parseInt(monthNum) - 1);
      return date.toLocaleDateString('es-AR', { month: 'short', year: '2-digit' });
    }),
    datasets: comparisonData.categories.map((category, index) => {
      const colors = [
        '#2563eb', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#a21caf', 
        '#f43f5e', '#6366f1', '#f472b6', '#0ea5e9', '#a3e635', '#fbbf24'
      ];
      return {
        label: category,
        data: comparisonData.categoryData[category],
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length] + '20', // Transparencia del 20%
        fill: false,
        tension: 0.1,
        pointRadius: 4,
        pointHoverRadius: 6,
      };
    }),
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: false, // Quitar título
      },
      legend: {
        display: false, // Quitar etiquetas superiores
      },
      tooltip: {
        mode: 'nearest',
        intersect: false,
        axis: 'xy',
        callbacks: {
          title: (items) => {
            if (!items?.length) return '';
            // Mostrar el mes como título
            return items[0].label;
          },
          label: (context) => {
            // Mostrar valor del mes seleccionado
            const value = context.parsed.y || 0;
            return `Mes: $${Number(value).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;
          },
          afterBody: (items) => {
            if (!items?.length) return '';
            const datasetLabel = items[0].dataset.label; // categoría
            if (!datasetLabel) return '';
            const series = comparisonData.categoryData[datasetLabel] || [];
            const total = series.reduce((sum, v) => sum + v, 0);
            return [`Total anual ${datasetLabel}: $${total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`];
          }
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#ffffff',
        borderWidth: 1,
        cornerRadius: 4,
        displayColors: false,
        padding: 8,
        caretSize: 5,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '$' + value.toLocaleString('es-AR');
          }
        }
      },
      x: {
        display: true,
        title: {
          display: true,
          text: 'Mes'
        }
      }
    },
    interaction: {
      mode: 'nearest',
      intersect: false,
      axis: 'xy',
    },
  };

  // Ordenar categorías por monto de mayor a menor para los gráficos
  const sortedCategoryEntries = Object.entries(categoryTotals).sort((a, b) => b[1] - a[1]);
  const sortedCategoryLabels = sortedCategoryEntries.map(([cat]) => cat);
  const sortedCategoryData = sortedCategoryEntries.map(([, val]) => val);

  const chartData = {
    labels: sortedCategoryLabels,
    datasets: [
      {
        label: 'Gasto por Categoría',
        data: sortedCategoryData,
        backgroundColor: [
          '#2563eb', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#a21caf', '#f43f5e'
        ],
      }
    ]
  };

  // Proyección de pagos por mes (cuotas de tarjeta de crédito)
  // Muestra SIEMPRE los próximos 12 meses sin aplicar filtros de fecha/categoría/búsqueda
  const monthlyPaymentMap = {};
  const monthlyPaymentDetails = {};

  const startMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const monthsHorizon = Array.from({ length: 12 }, (_, i) => formatYearMonth(addMonths(startMonth, i)));

  expenses.forEach(exp => {
    if (getPaymentMethod(exp) !== 'Tarjeta de Credito') return;
    const expenseDate = getLocalDateFromFirestore(exp.date);
    const installments = getInstallments(exp);
    const perInstallment = (exp.amount || 0) / installments;
    for (let i = 0; i < installments; i++) {
      const due = addMonths(new Date(expenseDate.getFullYear(), expenseDate.getMonth(), 1), i);
      const ym = formatYearMonth(due);
      if (!monthsHorizon.includes(ym)) continue;
      monthlyPaymentMap[ym] = (monthlyPaymentMap[ym] || 0) + perInstallment;
      if (!monthlyPaymentDetails[ym]) monthlyPaymentDetails[ym] = [];
      monthlyPaymentDetails[ym].push({
        description: (exp.description || '').trim() || 'Sin descripción',
        category: (exp.category || '').trim() || 'Sin categoría',
        installments,
        amountPerInstallment: perInstallment,
        expenseDate: expenseDate
      });
    }
  });

  const monthlyPaymentData = monthsHorizon.map(m => Math.round((monthlyPaymentMap[m] || 0) * 100) / 100);
  const totalCreditCardDebt = monthlyPaymentData.reduce((sum, value) => sum + value, 0);

  // Agrupar gastos por descripción para el gráfico de torta
  const descriptionTotals = {};
  expensesByMonth.forEach(exp => {
    const desc = (exp.description || 'Sin descripción').trim();
    descriptionTotals[desc] = (descriptionTotals[desc] || 0) + exp.amount;
  });

  // Ordenar descripciones por monto de mayor a menor para el gráfico de torta
  const sortedDescriptionEntries = Object.entries(descriptionTotals).sort((a, b) => b[1] - a[1]);
  const sortedDescriptionLabels = sortedDescriptionEntries.map(([desc]) => desc);
  const sortedDescriptionData = sortedDescriptionEntries.map(([, val]) => val);

  // Paleta y mapeos de color centralizados (se reutilizan en barras y donas)
  const categoryColorPalette = [
    '#2563eb', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#a21caf', '#f43f5e'
  ];
  const categoryColorMap = {};
  sortedCategoryLabels.forEach((cat, idx) => {
    categoryColorMap[cat] = categoryColorPalette[idx % categoryColorPalette.length];
  });
  const descToCategory = {};
  expensesByMonth.forEach(exp => {
    const desc = (exp.description || 'Sin descripción').trim();
    if (!descToCategory[desc] || exp.amount > (descToCategory[desc].amount || 0)) {
      descToCategory[desc] = { category: exp.category, amount: exp.amount };
    }
  });
  const descriptionColors = sortedDescriptionLabels.map(desc => categoryColorMap[descToCategory[desc]?.category] || '#888');
  const categoryPieColors = sortedCategoryLabels.map(cat => categoryColorMap[cat] || '#888');
  const totalItemsMes = sortedDescriptionData.reduce((sum, v) => sum + v, 0);

  // Opciones reutilizables para donas modernas con leyenda inferior
  const donutOptions = {
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: { color: '#c9d1d9', boxWidth: 12, padding: 12, font: { size: 11 } }
      },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.label}: $${Number(ctx.parsed).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
        }
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    cutout: '62%'
  };

  // Funciones de edición
  const openEditExpense = (exp) => {
    setEditExpense(exp);
    setEditExpenseForm({
      category: exp.category,
      amount: exp.amount,
      date: getDateStringFromFirestore(exp.date),
      description: exp.description || '',
      paymentMethod: getPaymentMethod(exp),
      installments: getInstallments(exp),
      excludeFromTotals: !!exp.excludeFromTotals
    });
  };

  const handleEditExpenseChange = e => {
    const { name, value } = e.target;
    setEditExpenseForm(f => ({ ...f, [name]: value }));
  };

  const handleEditExpenseSubmit = async e => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      const ref = doc(db, EXPENSES_COLLECTION, editExpense.id);
      await updateDoc(ref, {
        ...editExpenseForm,
        amount: parseFloat(editExpenseForm.amount),
        date: createFirestoreDate(editExpenseForm.date)
      });
      showNotification('Gasto actualizado', 'success');
      setEditExpense(null);
      fetchExpenses();
    } catch (err) {
      showNotification('Error al actualizar gasto', 'error');
    }
    setSavingEdit(false);
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar este gasto?')) return;
    try {
      await deleteDoc(doc(db, EXPENSES_COLLECTION, id));
      showNotification('Gasto eliminado', 'success');
      fetchExpenses();
    } catch (err) {
      showNotification('Error al eliminar gasto', 'error');
    }
  };

  // Función para alternar selección de un gasto
  const toggleExpenseSelection = (id) => {
    setSelectedExpenses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Función para seleccionar/deseleccionar todos los gastos filtrados
  const toggleAllExpenses = (isChecked) => {
    if (isChecked) {
      setSelectedExpenses(new Set(filteredExpenses.map(exp => exp.id)));
    } else {
      setSelectedExpenses(new Set());
    }
  };

  // Suma de los gastos seleccionados
  const selectedExpensesTotal = filteredExpenses
    .filter(exp => selectedExpenses.has(exp.id))
    .reduce((acc, exp) => acc + exp.amount, 0);

  // Función para renderizar gráficos de cheques
  const renderChequesChart = (chequesData, color = '#ef4444', title = 'Cheques pendientes por mes') => {
    const filteredCheques = chequesData.filter(cheq => !cheq.paid && !cheq.deposited && !cheq.archived);
    if (filteredCheques.length === 0) return <div className="no-data">No hay datos para mostrar</div>;

    const monthMap = {};
    filteredCheques.forEach(cheq => {
      const d = getLocalDateFromFirestore(cheq.dueDate);
      const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthMap[ym] = (monthMap[ym] || 0) + cheq.amount;
    });

    const months = Object.keys(monthMap).sort();
    const data = months.map(m => monthMap[m]);

    const barData = {
      labels: months,
      datasets: [
        {
          label: title,
          data,
          backgroundColor: color,
        }
      ]
    };

    return (
      <>
        <div className="expenses-chart-title">{title}</div>
        <div className="expenses-chart-container">
          <Bar data={barData} options={{
            plugins: { legend: { display: false } },
            responsive: true,
            scales: { y: { beginAtZero: true } }
          }} />
        </div>
        <div className="expenses-chart-table">
          <div className="expenses-chart-table-title">Totales por mes</div>
          <table className="expenses-mini-table">
            <thead>
              <tr>
                <th>Mes</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {months.map(m => (
                <tr key={m}>
                  <td>{m}</td>
                  <td>${monthMap[m].toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const toggleChequePaid = async (cheq) => {
    setProcessingCheques(prev => new Set(prev).add(cheq.id));
    try {
      const chequeRef = doc(db, CHEQUES_COLLECTION, cheq.id);
      await updateDoc(chequeRef, { paid: !cheq.paid, paidAt: !cheq.paid ? Timestamp.now() : null });
      showNotification(!cheq.paid ? 'Cheque marcado como pagado' : 'Cheque desmarcado como pagado', 'success');
      fetchCheques();
    } catch (e) {
      showNotification('Error al actualizar estado de pagado', 'error');
    }
    setProcessingCheques(prev => {
      const newSet = new Set(prev);
      newSet.delete(cheq.id);
      return newSet;
    });
  };

  const openEditCheque = (cheq) => {
    setEditCheque(cheq);
    setEditChequeType('emitted');
    setEditChequeForm({
      name: cheq.name,
      banco: cheq.banco,
      number: cheq.number,
      amount: cheq.amount,
      createdAt: getDateStringFromFirestore(cheq.createdAt),
      dueDate: getDateStringFromFirestore(cheq.dueDate)
    });
  };

  const handleEditChequeChange = e => {
    const { name, value } = e.target;
    setEditChequeForm(f => ({ ...f, [name]: value }));
  };

  const handleEditChequeSubmit = async e => {
    e.preventDefault();
    setSavingChequeEdit(true);
    try {
      const ref = doc(db, CHEQUES_COLLECTION, editCheque.id);
      await updateDoc(ref, {
        ...editChequeForm,
        amount: parseFloat(editChequeForm.amount),
        createdAt: createFirestoreDate(editChequeForm.createdAt),
        dueDate: createFirestoreDate(editChequeForm.dueDate)
      });
      showNotification('Cheque actualizado', 'success');
      setEditCheque(null);
      setEditChequeType(null);
      fetchCheques();
    } catch (err) {
      showNotification('Error al actualizar cheque', 'error');
    }
    setSavingChequeEdit(false);
  };

  const handleEditReceivedChequeSubmit = async e => {
    e.preventDefault();
    setSavingChequeEdit(true);
    try {
      const ref = doc(db, RECEIVED_CHEQUES_COLLECTION, editCheque.id);
      await updateDoc(ref, {
        ...editChequeForm,
        amount: parseFloat(editChequeForm.amount),
        receivedAt: createFirestoreDate(editChequeForm.receivedAt),
        dueDate: createFirestoreDate(editChequeForm.dueDate)
      });
      showNotification('Cheque recibido actualizado', 'success');
      setEditCheque(null);
      setEditChequeType(null);
      fetchReceivedCheques();
    } catch (err) {
      showNotification('Error al actualizar cheque recibido', 'error');
    }
    setSavingChequeEdit(false);
  };

  const toggleReceivedChequeDeposited = async (cheq) => {
    setProcessingCheques(prev => new Set(prev).add(cheq.id));
    try {
      const chequeRef = doc(db, RECEIVED_CHEQUES_COLLECTION, cheq.id);
      await updateDoc(chequeRef, { deposited: !cheq.deposited, depositedAt: !cheq.deposited ? Timestamp.now() : null });
      showNotification(!cheq.deposited ? 'Cheque marcado como depositado' : 'Cheque desmarcado como depositado', 'success');
      fetchReceivedCheques();
    } catch (e) {
      showNotification('Error al actualizar estado de depositado', 'error');
    }
    setProcessingCheques(prev => {
      const newSet = new Set(prev);
      newSet.delete(cheq.id);
      return newSet;
    });
  };

  const openEditReceivedCheque = (cheq) => {
    setEditCheque(cheq);
    setEditChequeType('received');
    setEditChequeForm({
      name: cheq.name,
      banco: cheq.banco,
      number: cheq.number,
      amount: cheq.amount,
      receivedAt: getDateStringFromFirestore(cheq.receivedAt),
      dueDate: getDateStringFromFirestore(cheq.dueDate)
    });
  };

  const deleteCheque = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar este cheque?')) return;
    try {
      await deleteDoc(doc(db, CHEQUES_COLLECTION, id));
      showNotification('Cheque eliminado', 'success');
      fetchCheques();
    } catch (err) {
      showNotification('Error al eliminar cheque', 'error');
    }
  };

  const deleteReceivedCheque = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar este cheque recibido?')) return;
    try {
      await deleteDoc(doc(db, RECEIVED_CHEQUES_COLLECTION, id));
      showNotification('Cheque recibido eliminado', 'success');
      fetchReceivedCheques();
    } catch (err) {
      showNotification('Error al eliminar cheque recibido', 'error');
    }
  };

  return (
    <div className="expenses-container">
      <nav className="expenses-tabs" role="tablist" aria-label="Secciones de gastos y cheques">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'expenses'}
          className={`expenses-tab${activeTab === 'expenses' ? ' active' : ''}`}
          onClick={() => setActiveTab('expenses')}
        >
          <span className="expenses-tab-icon">💰</span>
          <span className="expenses-tab-label">Gastos</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'cheques'}
          className={`expenses-tab${activeTab === 'cheques' ? ' active' : ''}`}
          onClick={() => setActiveTab('cheques')}
        >
          <span className="expenses-tab-icon">🏦</span>
          <span className="expenses-tab-label">Cheques emitidos</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'received-cheques'}
          className={`expenses-tab${activeTab === 'received-cheques' ? ' active' : ''}`}
          onClick={() => setActiveTab('received-cheques')}
        >
          <span className="expenses-tab-icon">💼</span>
          <span className="expenses-tab-label">Cheques en cartera</span>
        </button>
      </nav>

      <main className="expenses-main">
        {/* Pestaña de Gastos */}
        {activeTab === 'expenses' && (
          <div className="expenses-content-v2">
            {/* Formulario de carga: foco principal, flujo simple */}
            <section className="expenses-form-container">
              <div className="expenses-form-title">
                <FaPlus /> Registrar nuevo gasto
              </div>
              <form className="expenses-form" onSubmit={handleExpenseSubmit}>
                <div className="expenses-form-group">
                  <label className="expenses-label">Autocompletar desde un gasto previo</label>
                  <div className="expenses-form-search-container">
                    <span className="expenses-form-search-icon"><FaSearch /></span>
                    <input
                      className="expenses-input"
                      type="text"
                      placeholder="Buscar gasto previo..."
                      value={expenseForm.search || ''}
                      onChange={e => {
                        const value = e.target.value;
                        setExpenseForm(f => ({ ...f, search: value }));
                      }}
                      style={{ width: '100%' }}
                    />
                    {expenseForm.search && expenseForm.search.length > 1 && (
                      <div className="expenses-search-dropdown">
                        {expenses
                          .filter(e =>
                            (e.description && e.description.toLowerCase().includes(expenseForm.search.toLowerCase())) ||
                            (e.category && e.category.toLowerCase().includes(expenseForm.search.toLowerCase()))
                          )
                          .slice(0, 8)
                          .map(e => (
                            <div
                              key={e.id}
                              className="expenses-search-result"
                              onClick={() => {
                                setExpenseForm(f => ({
                                  ...f,
                                  category: e.category,
                                  description: e.description,
                                  search: '',
                                }));
                              }}
                            >
                              <span className="expenses-search-desc">{e.description}</span>
                              <span className="expenses-search-cat">({e.category})</span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="expenses-form-row">
                  <div className="expenses-form-group">
                    <label className="expenses-label">Categoría *</label>
                    <div className="expenses-selector-group">
                      <input
                        className="expenses-input"
                        type="text"
                        name="category"
                        value={expenseForm.category}
                        onChange={handleExpenseChange}
                        required
                        placeholder="Escribe o elige..."
                      />
                      <select
                        className="expenses-select"
                        value=""
                        onChange={e => {
                          if (e.target.value) setExpenseForm(f => ({ ...f, category: e.target.value }));
                        }}
                      >
                        <option value="">Elegir</option>
                        {dynamicCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="expenses-form-group">
                    <label className="expenses-label">Descripción</label>
                    <div className="expenses-selector-group">
                      <input
                        className="expenses-input"
                        type="text"
                        name="description"
                        value={expenseForm.description}
                        onChange={handleExpenseChange}
                        maxLength={100}
                        placeholder="Escribe o elige..."
                      />
                      <select
                        className="expenses-select"
                        value=""
                        onChange={e => {
                          if (e.target.value) setExpenseForm(f => ({ ...f, description: e.target.value }));
                        }}
                      >
                        <option value="">Elegir</option>
                        {usualExpenses.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="expenses-form-row">
                  <div className="expenses-form-group">
                    <label className="expenses-label">Monto *</label>
                    <input
                      className="expenses-input"
                      type="number"
                      name="amount"
                      value={expenseForm.amount}
                      onChange={handleExpenseChange}
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  <div className="expenses-form-group">
                    <label className="expenses-label">Fecha *</label>
                    <input
                      className="expenses-input"
                      type="date"
                      name="date"
                      value={expenseForm.date}
                      onChange={handleExpenseChange}
                      required
                    />
                  </div>
                </div>

                <div className="expenses-form-row">
                  <div className="expenses-form-group">
                    <label className="expenses-label">Medio de pago *</label>
                    <select
                      className="expenses-select"
                      name="paymentMethod"
                      value={expenseForm.paymentMethod}
                      onChange={handleExpenseChange}
                      required
                    >
                      <option value="Efectivo">Efectivo</option>
                      <option value="Transferencia">Transferencia</option>
                      <option value="Tarjeta de Debito">Tarjeta de Debito</option>
                      <option value="Tarjeta de Credito">Tarjeta de Credito</option>
                      <option value="Cheque">Cheque</option>
                    </select>
                  </div>
                  {expenseForm.paymentMethod === 'Tarjeta de Credito' && (
                    <div className="expenses-form-group">
                      <label className="expenses-label">Cuotas</label>
                      <select
                        className="expenses-select"
                        name="installments"
                        value={expenseForm.installments}
                        onChange={handleExpenseChange}
                      >
                        <option value={1}>1</option>
                        <option value={3}>3</option>
                        <option value={6}>6</option>
                        <option value={12}>12</option>
                      </select>
                    </div>
                  )}
                </div>

                <label className="expenses-exclude-row">
                  <CustomCheckbox
                    checked={!!expenseForm.excludeFromTotals}
                    onChange={(e) => setExpenseForm(f => ({ ...f, excludeFromTotals: e.target.checked }))}
                  />
                  <span className="expenses-hint">Excluir de totales y gráficos (pagos de resumen u otros movimientos)</span>
                </label>

                <button className="expenses-btn" type="submit">
                  <FaPlus /> Registrar Gasto
                </button>
              </form>
            </section>

            {/* Toggle de gráficos: se cargan solo bajo demanda */}
            <div className="expenses-analytics-toggle">
              <button
                type="button"
                className="expenses-btn-analytics"
                onClick={() => setShowCharts(v => !v)}
                aria-expanded={showCharts}
              >
                {showCharts ? <><FaEyeSlash /> Ocultar gráficos</> : <><FaChartBar /> Ver gastos y gráficos</>}
              </button>
            </div>

            {/* Panel de análisis: solo se renderiza al activarlo */}
            {showCharts && (
              <>
                <div className="expenses-charts-toolbar">
                  <label className="expenses-label" htmlFor="charts-month">Mes a analizar:</label>
                  <input
                    id="charts-month"
                    type="month"
                    value={selectedMonth}
                    onChange={e => setSelectedMonth(e.target.value)}
                    className="expenses-input"
                  />
                </div>
                <div className="expenses-charts-grid">
                  {/* Gastos por ítem (ocupa todo el ancho: suele tener más datos) */}
                  <div className="expenses-chart-card expenses-chart-card--wide">
                    <div className="expenses-chart-header"><h4>Gastos por ítem</h4></div>
                    <div className="expenses-chart-split">
                      <div className="expenses-chart-box">
                        <Bar
                          data={{
                            labels: sortedDescriptionLabels,
                            datasets: [{ label: 'Gasto por ítem', data: sortedDescriptionData, backgroundColor: descriptionColors }],
                          }}
                          options={{
                            plugins: {
                              legend: { display: false },
                              tooltip: {
                                callbacks: {
                                  title: (context) => context[0].label,
                                  label: (context) => `$${context.parsed.y.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`,
                                },
                              },
                            },
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: { y: { beginAtZero: true } }
                          }}
                        />
                      </div>
                      <div className="expenses-chart-box donut">
                        <Doughnut data={{
                          labels: sortedDescriptionLabels,
                          datasets: [{ data: sortedDescriptionData, backgroundColor: descriptionColors }],
                        }} options={donutOptions} />
                      </div>
                    </div>
                    <div className="expenses-total">
                      Total del mes: ${totalItemsMes.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </div>
                  </div>

                  {/* Gastos por categoría */}
                  <div className="expenses-chart-card">
                    <div className="expenses-chart-header"><h4>Gastos por categoría</h4></div>
                    <div className="expenses-chart-box">
                      <Bar data={chartData} options={{
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            callbacks: {
                              title: (context) => context[0].label,
                              label: (context) => `$${context.parsed.y.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`,
                            },
                          },
                        },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: { y: { beginAtZero: true } }
                      }} />
                    </div>
                    <div className="expenses-chart-box donut">
                      <Doughnut data={{
                        labels: sortedCategoryLabels,
                        datasets: [{ data: sortedCategoryData, backgroundColor: categoryPieColors }],
                      }} options={donutOptions} />
                    </div>
                    <div className="expenses-total">
                      Total del mes: ${totalGeneral.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </div>
                  </div>

                  {/* Medios de pago */}
                  <div className="expenses-chart-card">
                    <div className="expenses-chart-header"><h4>Medios de pago</h4></div>
                    <div className="expenses-chart-box donut">
                      <Doughnut data={{
                        labels: paymentMethodLabels,
                        datasets: [{ data: paymentMethodData, backgroundColor: ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'] }],
                      }} options={donutOptions} />
                    </div>
                  </div>

                  {/* Proyección tarjeta de crédito */}
                  <div className="expenses-chart-card">
                    <div className="expenses-chart-header"><h4>Tarjeta de crédito (12 meses)</h4></div>
                    <div className="expenses-chart-box">
                      <Bar
                        data={{
                          labels: monthsHorizon,
                          datasets: [{ label: 'Total a pagar', data: monthlyPaymentData, backgroundColor: '#0ea5e9' }]
                        }}
                        options={{
                          plugins: {
                            legend: { display: false },
                            tooltip: {
                              callbacks: {
                                title: (items) => (items?.length ? items[0].label : ''),
                                label: () => '',
                                afterBody: (items) => {
                                  if (!items?.length) return '';
                                  const ym = items[0].label;
                                  const rows = (monthlyPaymentDetails[ym] || []).map(d => {
                                    const amt = d.amountPerInstallment.toLocaleString('es-AR', { minimumFractionDigits: 2 });
                                    return `- ${d.description} (${d.category}): $${amt}`;
                                  });
                                  const total = (monthlyPaymentMap[ym] || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 });
                                  return rows.length ? [...rows, '', `Total: $${total}`] : [`Total: $${total}`];
                                }
                              }
                            }
                          },
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: { y: { beginAtZero: true } }
                        }}
                      />
                    </div>
                    <div className="expenses-total">
                      Deuda total: ${totalCreditCardDebt.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="expenses-table-container">
                <div className="expenses-table-header">
                  <h3>Gastos Filtrados</h3>
                </div>
                <div className="expenses-filters">
                  <div className="expenses-filter-group expenses-filter-group--full">
                    <label className="expenses-filter-label" htmlFor="expenses-search">Buscar</label>
                    <div className="expenses-input-icon">
                      <FaSearch className="expenses-input-icon-svg" />
                      <input
                        id="expenses-search"
                        type="text"
                        className="expenses-input"
                        placeholder="Buscar por categoría, descripción, monto o fecha..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="expenses-filter-group">
                    <label className="expenses-filter-label" htmlFor="category-filter">Categoría</label>
                    <select
                      id="category-filter"
                      className="expenses-select"
                      value={selectedCategory}
                      onChange={e => setSelectedCategory(e.target.value)}
                    >
                      <option value="">Selecciona una categoría</option>
                      <option value="Todas">Todas</option>
                      {[...dynamicCategories].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' })).map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div className="expenses-filter-group">
                    <label className="expenses-filter-label" htmlFor="payment-filter">Medio de pago</label>
                    <select
                      id="payment-filter"
                      className="expenses-select"
                      value={selectedPaymentMethod}
                      onChange={e => setSelectedPaymentMethod(e.target.value)}
                    >
                      <option value="">Selecciona</option>
                      <option value="Todos">Todos</option>
                      <option value="Efectivo">Efectivo</option>
                      <option value="Transferencia">Transferencia</option>
                      <option value="Tarjeta de Debito">Tarjeta de Debito</option>
                      <option value="Tarjeta de Credito">Tarjeta de Credito</option>
                      <option value="Cheque">Cheque</option>
                    </select>
                  </div>

                  <div className="expenses-filter-group">
                    <label className="expenses-filter-label" htmlFor="excluded-filter">Incluir en totales</label>
                    <select
                      id="excluded-filter"
                      className="expenses-select"
                      value={excludedFilter}
                      onChange={e => setExcludedFilter(e.target.value)}
                    >
                      <option value="Incluidos">Incluidos</option>
                      <option value="Excluidos">Excluidos</option>
                      <option value="Todos">Todos</option>
                    </select>
                  </div>

                  <div className="expenses-filter-group expenses-filter-group--range">
                    <label className="expenses-filter-label">Período</label>
                    <div className="expenses-filter-range">
                      <input
                        type="date"
                        id="date-from"
                        className="expenses-input"
                        value={dateFrom}
                        onChange={e => setDateFrom(e.target.value)}
                        aria-label="Desde"
                      />
                      <span className="expenses-filter-range-sep">—</span>
                      <input
                        type="date"
                        id="date-to"
                        className="expenses-input"
                        value={dateTo}
                        onChange={e => setDateTo(e.target.value)}
                        aria-label="Hasta"
                      />
                    </div>
                  </div>
                </div>
                {/* HINT DE SCROLL SOLO EN MOBILE */}
                <div className="expenses-table-scroll-hint">Desliza para ver más columnas →</div>
                <div className="expenses-table-responsive">
                  {loadingExpenses ? (
                    <div className="expenses-loading">Cargando gastos...</div>
                  ) : (
                    <table className="expenses-table">
                      <thead>
                        <tr>
                          <th>
                            <CustomCheckbox
                              checked={selectedExpenses.size > 0 && selectedExpenses.size === filteredExpenses.length}
                              onChange={e => toggleAllExpenses(e.target.checked)}
                            />
                          </th>
                          <th>Categoría</th>
                          <th>Descripción</th>
                          <th>Medio de pago</th>
                          <th>Fecha</th>
                          <th>Monto</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...filteredExpenses]
                          .sort((a, b) => {
                            const dateA = getLocalDateFromFirestore(a.date);
                            const dateB = getLocalDateFromFirestore(b.date);
                            return dateB - dateA; // Descendente: más reciente primero
                          })
                          .map(exp => (
                            <tr key={exp.id}>
                              <td>
                                <CustomCheckbox
                                  checked={selectedExpenses.has(exp.id)}
                                  onChange={() => toggleExpenseSelection(exp.id)}
                                />
                              </td>
                              <td>{exp.category}</td>
                              <td>
                                {exp.description}
                                {exp.excludeFromTotals && (
                                  <span
                                    className="expenses-badge"
                                    title="Este gasto está excluido de los totales y gráficos"
                                    style={{ marginLeft: '0.5rem', backgroundColor: '#6b7280', color: '#fff' }}
                                  >
                                    Excluido
                                  </span>
                                )}
                              </td>
                              <td>
                                <span>{getPaymentMethod(exp)}</span>
                                {getPaymentMethod(exp) === 'Tarjeta de Credito' && (() => {
                                  const inst = getInstallments(exp);
                                  const bg = getInstallmentsColor(inst);
                                  return (
                                    <span
                                      className="expenses-badge"
                                      title={`Pago en ${inst} ${inst === 1 ? 'cuota' : 'cuotas'}`}
                                      style={{ marginLeft: '0.5rem', backgroundColor: bg, color: '#fff' }}
                                    >
                                      {inst} {inst === 1 ? 'cuota' : 'cuotas'}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td>{getLocalDateFromFirestore(exp.date).toLocaleDateString('es-AR')}</td>
                              <td>${exp.amount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                              <td>
                                <div className="expenses-actions">
                                  <button
                                    className="expenses-btn-small"
                                    title="Editar gasto"
                                    onClick={() => openEditExpense(exp)}
                                  >
                                    <FaEdit />
                                  </button>
                                  <button
                                    className="expenses-btn-small expenses-btn-danger"
                                    title="Eliminar gasto"
                                    onClick={() => deleteExpense(exp.id)}
                                  >
                                    <FaTrash />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  )}
                </div>
                {selectedExpenses.size > 0 && (
                  <div className="expenses-selection-summary">
                    <span>Seleccionados: {selectedExpenses.size}</span>
                    <span>Total: ${selectedExpensesTotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                <div className="expenses-total">
                  Total filtrado: ${totalFiltrado.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                </div>
              </div>
          </div>
        )}

        {/* Pestaña de Cheques Emitidos */}
        {activeTab === 'cheques' && (
          <div className="expenses-cheques-layout">
            <div className="expenses-cheques-left">
              <div className="expenses-form-container">
                <form className="expenses-form" onSubmit={handleChequeSubmit}>
                  <div className="expenses-form-group">
                    <label className="expenses-label">Nombre Destinatario *</label>
                    <input
                      className="expenses-input"
                      type="text"
                      name="name"
                      value={chequeForm.name}
                      onChange={handleChequeChange}
                      required
                    />
                  </div>

                  <div className="expenses-form-group">
                    <label className="expenses-label">Banco Emisor *</label>
                    <input className="expenses-input" type="text" name="banco" value={chequeForm.banco} onChange={handleChequeChange} required />
                  </div>

                  <div className="expenses-form-row-2">
                    <div className="expenses-form-group">
                      <label className="expenses-label">Número de Cheque *</label>
                      <input
                        className="expenses-input"
                        type="text"
                        name="number"
                        value={chequeForm.number}
                        onChange={handleChequeChange}
                        required
                      />
                    </div>
                    <div className="expenses-form-group">
                      <label className="expenses-label">Monto *</label>
                      <input
                        className="expenses-input"
                        type="number"
                        name="amount"
                        value={chequeForm.amount}
                        onChange={handleChequeChange}
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>

                  <div className="expenses-form-row">
                    <div className="expenses-form-group">
                      <label className="expenses-label">Fecha de Emisión *</label>
                      <input
                        className="expenses-input"
                        type="date"
                        name="createdAt"
                        value={chequeForm.createdAt}
                        onChange={handleChequeChange}
                        required
                      />
                    </div>
                    <div className="expenses-form-group">
                      <label className="expenses-label">Fecha de Vencimiento *</label>
                      <input
                        className="expenses-input"
                        type="date"
                        name="dueDate"
                        value={chequeForm.dueDate}
                        onChange={handleChequeChange}
                        required
                      />
                    </div>
                  </div>

                  <button className="expenses-btn" type="submit">
                    <FaPlus /> Registrar Cheque
                  </button>
                </form>
              </div>

              <div className="expenses-table-container">
                <div className="expenses-table-header">
                  <h3>Cheques Emitidos</h3>
                  <button
                    className="expenses-btn-toggle"
                    onClick={() => setShowArchivedCheques(v => !v)}
                    type="button"
                  >
                    {showArchivedCheques ? (
                      <><FaEye /> Ver Activos</>
                    ) : (
                      <><FaEyeSlash /> Ver Archivados</>
                    )}
                  </button>
                </div>

                <div className="expenses-table-responsive">
                  {loadingCheques ? (
                    <div className="expenses-loading">Cargando cheques...</div>
                  ) : (
                    <table className="expenses-table cheques-table">
                      <thead>
                        <tr>
                          <th className="col-checkbox">
                            <CustomCheckbox
                              checked={selectedCheques.size > 0 && selectedCheques.size === cheques.filter(cheq => showArchivedCheques ? cheq.archived : !cheq.archived).length}
                              onChange={(e) => toggleAllCheques(e.target.checked)}
                            />
                          </th>
                          <th className="col-beneficiario">Beneficiario</th>
                          <th className="col-numero">N°</th>
                          <th className="col-monto">Monto</th>
                          <th className="col-vencimiento">Vence</th>
                          <th className="col-estado">Estado</th>
                          <th className="col-actions">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cheques
                          .filter(cheq => showArchivedCheques ? cheq.archived : !cheq.archived)
                          .map(cheq => (
                            <tr key={cheq.id} className={cheq.paid ? 'row-paid' : ''}>
                              <td className="col-checkbox">
                                <CustomCheckbox
                                  checked={selectedCheques.has(cheq.id)}
                                  onChange={() => toggleChequeSelection(cheq.id)}
                                />
                              </td>
                              <td className="col-beneficiario">
                                <div className="beneficiario-info">
                                  <div className="beneficiario-name">{cheq.name}</div>
                                  <div className="beneficiario-bank">{cheq.banco}</div>
                                </div>
                              </td>
                              <td className="col-numero">{cheq.number}</td>
                              <td className="col-monto">
                                <span className="monto-value">
                                  ${cheq.amount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                </span>
                              </td>
                              <td className="col-vencimiento">
                                {getLocalDateFromFirestore(cheq.dueDate).toLocaleDateString('es-AR')}
                              </td>
                              <td className="col-estado">
                                <StatusBadge dueDate={cheq.dueDate} archived={cheq.archived} />
                              </td>
                              <td className="col-actions">
                                <div className="action-buttons">
                                  <StatusToggle
                                    checked={!!cheq.paid}
                                    onChange={() => toggleChequePaid(cheq)}
                                    disabled={processingCheques.has(cheq.id)}
                                    type="paid"
                                    size="small"
                                  />
                                  <button
                                    className="action-btn edit"
                                    title="Editar cheque"
                                    onClick={() => openEditCheque(cheq)}
                                  >
                                    <FaEdit />
                                  </button>
                                  <button
                                    className={`action-btn ${cheq.archived ? 'unarchive' : 'archive'}`}
                                    disabled={processingCheques.has(cheq.id)}
                                    onClick={() => toggleChequeArchived(cheq.id, cheq.archived, CHEQUES_COLLECTION)}
                                    title={cheq.archived ? "Desarchivar" : "Archivar"}
                                  >
                                    {cheq.archived ? <FaUndo /> : <FaArchive />}
                                  </button>
                                  <button
                                    className="action-btn delete"
                                    title="Eliminar cheque"
                                    onClick={() => deleteCheque(cheq.id)}
                                  >
                                    <FaTrash />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
              {selectedCheques.size > 0 && (
                <div className="expenses-selection-summary">
                  <span>Cheques seleccionados: {selectedCheques.size}</span>
                  <span>Total: ${selectedChequesTotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                </div>
              )}
            </div>

            <div className="expenses-cheques-panel">
              {renderChequesChart(cheques, '#ef4444', 'Cheques pendientes por mes')}
            </div>
          </div>
        )}

        {/* Pestaña de Cheques Recibidos */}
        {activeTab === 'received-cheques' && (
          <div className="expenses-cheques-layout">
            <div className="expenses-cheques-left">
              <div className="expenses-form-container">
                <form className="expenses-form" onSubmit={handleReceivedChequeSubmit}>
                  <div className="expenses-form-group">
                    <label className="expenses-label">Nombre del Emisor *</label>
                    <input
                      className="expenses-input"
                      type="text"
                      name="name"
                      value={receivedChequeForm.name}
                      onChange={handleReceivedChequeChange}
                      required
                    />
                  </div>

                  <div className="expenses-form-group">
                    <label className="expenses-label">Banco Emisor *</label>
                    <input className="expenses-input" type="text" name="banco" value={receivedChequeForm.banco} onChange={handleReceivedChequeChange} required />
                  </div>

                  <div className="expenses-form-row-2">
                    <div className="expenses-form-group">
                      <label className="expenses-label">Número de Cheque *</label>
                      <input
                        className="expenses-input"
                        type="text"
                        name="number"
                        value={receivedChequeForm.number}
                        onChange={handleReceivedChequeChange}
                        required
                      />
                    </div>
                    <div className="expenses-form-group">
                      <label className="expenses-label">Monto *</label>
                      <input
                        className="expenses-input"
                        type="number"
                        name="amount"
                        value={receivedChequeForm.amount}
                        onChange={handleReceivedChequeChange}
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>

                  <div className="expenses-form-row">
                    <div className="expenses-form-group">
                      <label className="expenses-label">Fecha de Recepción *</label>
                      <input
                        className="expenses-input"
                        type="date"
                        name="receivedAt"
                        value={receivedChequeForm.receivedAt}
                        onChange={handleReceivedChequeChange}
                        required
                      />
                    </div>
                    <div className="expenses-form-group">
                      <label className="expenses-label">Fecha de Vencimiento *</label>
                      <input
                        className="expenses-input"
                        type="date"
                        name="dueDate"
                        value={receivedChequeForm.dueDate}
                        onChange={handleReceivedChequeChange}
                        required
                      />
                    </div>
                  </div>

                  <button className="expenses-btn" type="submit">
                    <FaPlus /> Registrar Cheque Recibido
                  </button>
                </form>
              </div>

              <div className="expenses-table-container">
                <div className="expenses-table-header">
                  <h3>Cheques en cartera</h3>
                  <button
                    className="expenses-btn-toggle"
                    onClick={() => setShowArchivedReceivedCheques(v => !v)}
                    type="button"
                  >
                    {showArchivedReceivedCheques ? (
                      <><FaEye /> Ver Disponibles</>
                    ) : (
                      <><FaEyeSlash /> Ver Archivados</>
                    )}
                  </button>
                </div>

                {selectedReceivedCheques.size > 0 && (
                  <div className="expenses-selection-summary">
                    <span>Cheques seleccionados: {selectedReceivedCheques.size}</span>
                    <span>Total: ${selectedReceivedChequesTotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}

                <div className="expenses-table-responsive">
                  {loadingReceivedCheques ? (
                    <div className="expenses-loading">Cargando cheques recibidos...</div>
                  ) : (
                    <table className="expenses-table cheques-table">
                      <thead>
                        <tr>
                          <th className="col-checkbox">
                            <CustomCheckbox
                              checked={selectedReceivedCheques.size > 0 && selectedReceivedCheques.size === receivedCheques.filter(cheq => showArchivedReceivedCheques ? cheq.archived : !cheq.archived).length}
                              onChange={(e) => toggleAllReceivedCheques(e.target.checked)}
                            />
                          </th>
                          <th className="col-beneficiario">Emisor</th>
                          <th className="col-numero">N°</th>
                          <th className="col-monto">Monto</th>
                          <th className="col-vencimiento">Vence</th>
                          <th className="col-estado">Estado</th>
                          <th className="col-actions">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {receivedCheques
                          .filter(cheq => showArchivedReceivedCheques ? cheq.archived : !cheq.archived)
                          .map(cheq => (
                            <tr key={cheq.id} className={cheq.deposited ? 'row-deposited' : ''}>
                              <td className="col-checkbox">
                                <CustomCheckbox
                                  checked={selectedReceivedCheques.has(cheq.id)}
                                  onChange={() => toggleReceivedChequeSelection(cheq.id)}
                                />
                              </td>
                              <td className="col-beneficiario">
                                <div className="beneficiario-info">
                                  <div className="beneficiario-name">{cheq.name}</div>
                                  <div className="beneficiario-bank">{cheq.banco}</div>
                                </div>
                              </td>
                              <td className="col-numero">{cheq.number}</td>
                              <td className="col-monto">
                                <span className="monto-value">
                                  ${cheq.amount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                </span>
                              </td>
                              <td className="col-vencimiento">
                                {getLocalDateFromFirestore(cheq.dueDate).toLocaleDateString('es-AR')}
                              </td>
                              <td className="col-estado">
                                <StatusBadge dueDate={cheq.dueDate} archived={cheq.archived} />
                              </td>
                              <td className="col-actions">
                                <div className="action-buttons">
                                  <StatusToggle
                                    checked={!!cheq.deposited}
                                    onChange={() => toggleReceivedChequeDeposited(cheq)}
                                    disabled={processingCheques.has(cheq.id)}
                                    type="deposited"
                                    size="small"
                                  />
                                  <button
                                    className="action-btn edit"
                                    title="Editar cheque recibido"
                                    onClick={() => openEditReceivedCheque(cheq)}
                                  >
                                    <FaEdit />
                                  </button>
                                  <button
                                    className={`action-btn ${cheq.archived ? 'unarchive' : 'archive'}`}
                                    disabled={processingCheques.has(cheq.id)}
                                    onClick={() => toggleChequeArchived(cheq.id, cheq.archived, RECEIVED_CHEQUES_COLLECTION)}
                                    title={cheq.archived ? "Desarchivar" : "Archivar"}
                                  >
                                    {cheq.archived ? <FaUndo /> : <FaArchive />}
                                  </button>
                                  <button
                                    className="action-btn delete"
                                    title="Eliminar cheque recibido"
                                    onClick={() => deleteReceivedCheque(cheq.id)}
                                  >
                                    <FaTrash />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>

            <div className="expenses-cheques-panel">
              {renderChequesChart(receivedCheques, '#10b981', 'Cheques disponibles por mes')}
            </div>
          </div>
        )}
      </main>

      {/* Sección de Comparación Mensual: solo visible al activar gráficos en la pestaña de Gastos */}
      {activeTab === 'expenses' && showCharts && (
      <section className="expenses-comparison-section">
        <div className="expenses-comparison-container">
          <div className="expenses-comparison-header">
            <h2>📊 Comparación Mensual por Categoría</h2>
            <div className="expenses-comparison-controls">
              <div className="expenses-comparison-control-group">
                <label className="expenses-label">Año:</label>
                <input
                  type="number"
                  value={comparisonYear}
                  onChange={e => setComparisonYear(parseInt(e.target.value || new Date().getFullYear(), 10))}
                  className="expenses-input"
                  style={{ width: '100px' }}
                />
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    type="button"
                    className="expenses-btn-small"
                    onClick={() => setComparisonYear(y => (y || new Date().getFullYear()) - 1)}
                    title="Año anterior"
                  >
                    -
                  </button>
                  <button
                    type="button"
                    className="expenses-btn-small"
                    onClick={() => setComparisonYear(y => (y || new Date().getFullYear()) + 1)}
                    title="Año siguiente"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          </div>

          {comparisonData.categories.length > 0 ? (
            <div className="expenses-comparison-content">
              <div className="expenses-comparison-chart">
                <div style={{ height: '500px', width: '100%' }}>
                  <Line data={lineChartData} options={lineChartOptions} />
                </div>
              </div>
              
              <div className="expenses-comparison-summary">
                <h4>Resumen del Período</h4>
                <div className="expenses-comparison-stats">
                  <div className="expenses-comparison-stat">
                    <span className="stat-label">Categorías:</span>
                    <span className="stat-value">{comparisonData.categories.length}</span>
                  </div>
                  <div className="expenses-comparison-stat">
                    <span className="stat-label">Meses:</span>
                    <span className="stat-value">{comparisonData.months.length}</span>
                  </div>
                  <div className="expenses-comparison-stat">
                    <span className="stat-label">Total del Período:</span>
                    <span className="stat-value">
                      ${comparisonData.categories.reduce((total, category) => 
                        total + comparisonData.categoryData[category].reduce((sum, monthValue) => sum + monthValue, 0), 0
                      ).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
                
                <div className="expenses-comparison-table">
                  <h5>Totales por Categoría</h5>
                  <table className="expenses-mini-table">
                    <thead>
                      <tr>
                        <th>Categoría</th>
                        <th>Total</th>
                        <th>Promedio Mensual</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.categories
                        .map(category => ({
                          category,
                          total: comparisonData.categoryData[category].reduce((sum, value) => sum + value, 0)
                        }))
                        .sort((a, b) => b.total - a.total)
                        .map(({ category, total }) => (
                          <tr key={category}>
                            <td>{category}</td>
                            <td>${total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                            <td>${(total / comparisonData.months.length).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="expenses-comparison-empty">
              <p>No hay datos disponibles para el período seleccionado.</p>
              <p>Intenta ajustar las fechas o agregar gastos en el período.</p>
            </div>
          )}
        </div>
      </section>
      )}

      {/* Notificaciones */}
      {/* Eliminar el renderizado de la notificación local */}

      {/* Modal de edición de gasto */}
      {editExpense && (
        <div className="expenses-modal-overlay">
          <form className="expenses-modal-form" onSubmit={handleEditExpenseSubmit}>
            <h3>Editar Gasto</h3>
            <div className="expenses-form-group">
              <label className="expenses-label">Categoría *</label>
              <select
                className="expenses-select"
                name="category"
                value={editExpenseForm.category}
                onChange={handleEditExpenseChange}
                required
              >
                <option value="">Selecciona una categoría</option>
                {dynamicCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
            <div className="expenses-form-group">
              <label className="expenses-label">Monto *</label>
              <input
                className="expenses-input"
                type="number"
                name="amount"
                value={editExpenseForm.amount}
                onChange={handleEditExpenseChange}
                min="0"
                step="0.01"
                required
              />
            </div>
            <div className="expenses-form-group">
              <label className="expenses-label">Fecha *</label>
              <input
                className="expenses-input"
                type="date"
                name="date"
                value={editExpenseForm.date}
                onChange={handleEditExpenseChange}
                required
              />
            </div>
            <div className="expenses-form-group">
              <label className="expenses-label">Descripción</label>
              <input
                className="expenses-input"
                type="text"
                name="description"
                value={editExpenseForm.description}
                onChange={handleEditExpenseChange}
                maxLength={100}
              />
            </div>
            <div className="expenses-form-group">
              <label className="expenses-label">Excluir de totales</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CustomCheckbox
                  checked={!!editExpenseForm.excludeFromTotals}
                  onChange={(e) => setEditExpenseForm(f => ({ ...f, excludeFromTotals: e.target.checked }))}
                />
                <span className="expenses-hint">No impacta gráficos ni totales</span>
              </div>
            </div>
            <div className="expenses-form-group">
              <label className="expenses-label">Medio de pago *</label>
              <select
                className="expenses-select"
                name="paymentMethod"
                value={editExpenseForm.paymentMethod}
                onChange={handleEditExpenseChange}
                required
              >
                <option value="Efectivo">Efectivo</option>
                <option value="Transferencia">Transferencia</option>
                <option value="Tarjeta de Debito">Tarjeta de Debito</option>
                <option value="Tarjeta de Credito">Tarjeta de Credito</option>
                <option value="Cheque">Cheque</option>
              </select>
            </div>
            {editExpenseForm.paymentMethod === 'Tarjeta de Credito' && (
              <div className="expenses-form-group">
                <label className="expenses-label">Cuotas</label>
                <select
                  className="expenses-select"
                  name="installments"
                  value={editExpenseForm.installments}
                  onChange={handleEditExpenseChange}
                >
                  <option value={1}>1</option>
                  <option value={3}>3</option>
                  <option value={6}>6</option>
                  <option value={12}>12</option>
                </select>
              </div>
            )}
            <div className="expenses-modal-actions">
              <button className="expenses-btn" type="submit" disabled={savingEdit}>
                {savingEdit ? 'Guardando...' : 'Guardar'}
              </button>
              <button
                className="expenses-btn-small"
                type="button"
                onClick={() => setEditExpense(null)}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Modal de edición de cheque */}
      {editCheque && (
        <div className="expenses-modal-overlay">
          <form className="expenses-modal-form" onSubmit={editChequeType === 'received' ? handleEditReceivedChequeSubmit : handleEditChequeSubmit}>
            <h3>{editChequeType === 'received' ? 'Editar Cheque Recibido' : 'Editar Cheque Emitido'}</h3>
            <div className="expenses-form-group">
              <label className="expenses-label">Nombre *</label>
              <input
                className="expenses-input"
                type="text"
                name="name"
                value={editChequeForm.name}
                onChange={handleEditChequeChange}
                required
              />
            </div>
            <div className="expenses-form-group">
              <label className="expenses-label">Banco *</label>
              <input
                className="expenses-input"
                type="text"
                name="banco"
                value={editChequeForm.banco}
                onChange={handleEditChequeChange}
                required
              />
            </div>
            <div className="expenses-form-row-2">
              <div className="expenses-form-group">
                <label className="expenses-label">Número *</label>
                <input
                  className="expenses-input"
                  type="text"
                  name="number"
                  value={editChequeForm.number}
                  onChange={handleEditChequeChange}
                  required
                />
              </div>
              <div className="expenses-form-group">
                <label className="expenses-label">Monto *</label>
                <input
                  className="expenses-input"
                  type="number"
                  name="amount"
                  value={editChequeForm.amount}
                  onChange={handleEditChequeChange}
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            </div>
            <div className="expenses-form-row">
              <div className="expenses-form-group">
                <label className="expenses-label">
                  {editChequeType === 'received' ? 'Fecha de Recepción *' : 'Fecha de Emisión *'}
                </label>
                <input
                  className="expenses-input"
                  type="date"
                  name={editChequeType === 'received' ? 'receivedAt' : 'createdAt'}
                  value={editChequeType === 'received' ? editChequeForm.receivedAt : editChequeForm.createdAt}
                  onChange={handleEditChequeChange}
                  required
                />
              </div>
              <div className="expenses-form-group">
                <label className="expenses-label">Fecha de Vencimiento *</label>
                <input
                  className="expenses-input"
                  type="date"
                  name="dueDate"
                  value={editChequeForm.dueDate}
                  onChange={handleEditChequeChange}
                  required
                />
              </div>
            </div>
            <div className="expenses-modal-actions">
              <button className="expenses-btn" type="submit" disabled={savingChequeEdit}>
                {savingChequeEdit ? 'Guardando...' : 'Guardar'}
              </button>
              <button
                className="expenses-btn-small"
                type="button"
                onClick={() => {
                  setEditCheque(null);
                  setEditChequeType(null);
                }}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminExpenses;